<template>
  <div class="conference-booker">
    <div class="conference-booker__main">
      <template v-if="invalidConfig">
        <strong>{{ noPricingScheduleMessage }}</strong>
      </template>
      <template v-else>
        <template v-if="volumePricingOptions && volumePricingOptions.length">
          <label for="volume-pricing-options">
            To book places for this conference, please select the level
            of class access you require:
          </label>

          <div class="form-group">
            <b-form-radio-group
              id="volume-pricing-options"
              v-model="selectedBand"
              :disabled="hasEventPassed"
              stacked>
              <b-form-radio
                v-for="(option, index) in volumePricingOptions"
                :key="index"
                :value="option.value">
                {{ option.text }}
                <span class="float-right">
                  {{ renderPrice(option.price) }}
                </span>
              </b-form-radio>
            </b-form-radio-group>
          </div>
        </template>
        <template v-else>
          <p>
            To book places for this conference, please complete the fields below
          </p>

          <div class="form-group">
            <label class="conference-booker__label">
              Number of students:
              <span class="conference-booker__price">
                {{ renderPrice(studentPriceValue) }} each
              </span>
            </label>
            <input v-model.number="numStudents"
                   class="form-control"
                   :disabled="hasEventPassed"
                   type="number"
                   min="0">
            <p v-if="!digital">
              Which gives you {{ numFreeTeachers }} free teacher place(s)
            </p>
          </div>

          <div class="form-group">
            <label class="conference-booker__label">
              <template v-if="digital">
                Teacher places:
              </template>
              <template v-else>
                Additional teacher places:
              </template>

              <span class="conference-booker__price">
                {{ renderPrice(teacherPriceValue) }} each
              </span>
            </label>
            <input v-model.number="numTeachers"
                   class="form-control"
                   :disabled="hasEventPassed"
                   type="number"
                   min="0">
          </div>
        </template>

        <div class="form-group">
          <table class="conference-booker__prices">
            <tbody>
              <template v-if="!volumePricingOptions || !volumePricingOptions.length">
                <tr>
                  <th>
                    Total student places
                  </th>
                  <td class="conference-booker__quantity">
                    {{ numStudents }}
                  </td>
                  <td class="conference-booker__subtotal">
                    {{ renderPrice(studentSubtotal) }}
                  </td>
                </tr>

                <tr v-if="( !volumePricingOptions && !volumePricingOptions.length ) || !digital">
                  <th>
                    Free teacher places
                  </th>
                  <td class="conference-booker__quantity">
                    {{ numFreeTeachers }}
                  </td>
                  <td class="conference-booker__subtotal">
                    {{ renderPrice(0) }}
                  </td>
                </tr>

                <tr>
                  <th>
                    <template v-if="digital">
                      Teacher places
                    </template>
                    <template v-else>
                      Additional teacher places
                    </template>
                  </th>
                  <td class="conference-booker__quantity">
                    {{ numTeachers }}
                  </td>
                  <td class="conference-booker__subtotal">
                    {{ renderPrice(teacherSubtotal) }}
                  </td>
                </tr>
              </template>
            </tbody>

            <tfoot>
              <tr>
                <td colspan="2">
                  Total
                </td>
                <td class="conference-booker__subtotal">
                  {{ renderPrice(total) }}
                </td>
              </tr>
            </tfoot>
          </table>
          <div v-if="userMessage && !purchaseAllowed"
               class="conference-booker__warning-message">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-html="userMessage"></span>
          </div>
          <div><slot name="deposit" /></div>
        </div>

        <div v-if="!disabled"
             class="form-group">
          <b-form-checkbox v-model="agreedToTerms"
                           :disabled="hasEventPassed">
            <div v-html="termsAndConditionsText"></div>
          </b-form-checkbox>
        </div>
        <div v-else>
          <strong v-if="webUserTypeId === 0 || webUserTypeId === 8"
                  class="user-type-message">
            To purchase, please sign in to your UK Educator Admin (purchasing),
            UK Teacher - Home Address Only, UK Private Individual or Overseas account
          </strong>
          <div v-else-if="webUserTypeId === 11"
               class="user-type-message">
            <strong>
              You are logged into a UK Educator Standard (non-purchasing) account.
              You are not be able to place orders with this sort of account.
            </strong>

            <strong>
              If you need to buy something for your school, please contact a colleague with a UK
              Educator Admin account. You can see a list of colleagues with this sort of
              account <a :href="ourAdminUsersUrl">here</a>.
            </strong>

            <strong>
              If you want to buy a publication for yourself, please register a UK Individual
              Teacher account.
            </strong>
          </div>
        </div>
        <button class="btn conference-booker__submit"
                :disabled="disabled
                  || !agreedToTerms
                  || (!numStudents && !numTeachers && !numPlaces)
                  || hasEventPassed
                  || !purchaseAllowed"
                @click="showBookingModal">
          <template v-if="loading">
            <font-awesome-icon icon="spinner"
                               spin></font-awesome-icon>
          </template>
          <template v-else-if="submitted">
            <font-awesome-icon icon="check"></font-awesome-icon>
            Added!
          </template>
          <template v-else-if="error">
            <font-awesome-icon :icon="['far', 'times']"></font-awesome-icon>
            Error
          </template>
          <template v-else>
            <font-awesome-icon icon="chevron-right"></font-awesome-icon>
            Book now
          </template>
        </button>

        <strong v-if="hasEventConcluded">
          This conference has already taken place and cannot be booked
        </strong>
        <strong v-else-if="hasEventPassed">
          The last booking date has passed therefore this conference cannot be booked
        </strong>

        <p
          v-if="digital && ( !volumePricingOptions || !volumePricingOptions.length )"
          class="conference-booker__contact-us">
          Please contact us to discuss bespoke requirements if
          you are a small school with less than {{ smallSchoolPupilCount }} pupils.
        </p>
      </template>
    </div>

    <ConferenceBookingModal
      :show="showModal"
      :conference-title="instance.instanceName"
      :instance="instance"
      :web-account-id="webAccountId"
      :basket-url="basketUrl"
      hide-add-details-later
      :paid-teacher-places="numTeachers"
      :free-teacher-places="numFreeTeachers"
      :products="products"
      :product-id="productId"
      :instance-id="instanceId"
      @close="showModal = false">
    </ConferenceBookingModal>

    <CustomModal
      :show="showErrorModal"
      @close="closeErrorModal">
      <template #header>
        <h2 class="confirm-modal__heading">
          Error Booking
        </h2>
      </template>
      <div class="confirm-modal__body">
        <p>The following error has occurred:</p>
        <p>{{ errorMessage || "Something went wrong while submitting the booking." }}</p>
      </div>

      <template #footer>
        <div class="confirm-modal__actions">
          <button
            class="btn btn-accent-1"
            type="button"
            @click="closeErrorModal">
            Close
          </button>
        </div>
      </template>
    </CustomModal>
  </div>
</template>

<script>
import { EcommerceModule } from "spiritweb.packages.ecommerce";
import CustomModal from "~/Components/CustomModal.vue";
import ConferenceBookingModal from "../Conference/ConferenceBookingModal.vue";

export default {
  name: "ConferenceBooker",
  components: {
    CustomModal,
    ConferenceBookingModal,
  },
  props: {
    productId: {
      type: Number,
      default: null,
    },
    instanceId: {
      type: String,
      default: null,
    },
    defaultPrice: {
      type: Object,
      default: () => {
      },
    },
    studentPrice: {
      type: Object,
      default: () => {
      },
    },
    teacherPrice: {
      type: Object,
      default: () => {
      },
    },
    digital: {
      type: Boolean,
      default: false,
    },
    smallSchoolPupilCount: {
      type: Number,
      default: 10,
    },
    basketUrl: {
      type: String,
      default: "/basket",
    },
    noPricingScheduleMessage: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    webUserTypeId: {
      type: Number,
      default: null,
    },
    ourAdminUsersUrl: {
      type: String,
      default: null,
    },
    hasEventPassed: {
      type: Boolean,
      required: true,
      default: true,
    },
    hasEventConcluded: {
      type: Boolean,
      required: true,
      default: true,
    },
    instance: {
      type: Object,
      required: true,
    },
    webAccountId: {
      type: String,
      default: null,
    },
    termsAndConditionsText: {
      type: String,
      default: "I have read the cancellation policy. Please see under booking details.",
    },
    userMessage: {
      type: String,
      default: null,
    },
    purchaseAllowed: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ( {
    orderLineIds: [],
    selectedBand: null,
    numStudents: 0,
    numTeachers: 0,
    numPlaces: 0,
    agreedToTerms: false,
    loading: false,
    submitted: false,
    submitAttempted: false,
    error: false,
    errorMessage: null,
    showModal: false,
    showErrorModal: false,
    formData: {
      name: null,
      email: null,
      phone: null,
    },
  } ),
  computed: {
    volumePricingOptions () {
      let result = [];

      if ( this.defaultPrice?.pricingBands?.key === 1 ) {
        result = this.defaultPrice.pricingBands.value.map( x => ( {
          value: x.productPriceBandId,
          text: `Access for ${x.quantityLower}${x.quantityHigher === -1 ? "+" : `-${x.quantityHigher}`} classes`,
          price: x.priceExcVat,
          quantityLower: x.quantityLower,
          quantityHigher: x.quantityHigher,
        } ) );
      }

      return result;
    },
    studentPriceValue () {
      let result = 0;

      if ( this.studentPrice ) {
        result = this.studentPrice.priceExcVat;
      }

      return result;
    },
    teacherPriceValue () {
      let result = 0;

      if ( this.teacherPrice ) {
        result = this.teacherPrice.priceExcVat;
      }

      return result;
    },
    numFreeTeachers () {
      let result = 0;

      if ( this.numStudents >= 100 ) {
        result = 11;
      } else {
        let division = Math.floor( this.numStudents / 10 );

        if ( this.numStudents > 0 && !this.digital ) {
          division += 1;
        }

        result = Math.max( division, 0 );
      }

      return result;
    },
    studentSubtotal () {
      return this.numStudents * this.studentPriceValue;
    },
    teacherSubtotal () {
      return this.numTeachers * this.teacherPriceValue;
    },
    volumeSubtotal () {
      let result = 0;

      if ( this.volumePricingOptions ) {
        for ( let i = 0; i < this.volumePricingOptions.length; i += 1 ) {
          const band = this.volumePricingOptions[i];
          if ( this.numPlaces >= band.quantityLower
            && ( this.numPlaces <= band.quantityHigher || band.quantityHigher === -1 ) ) {
            result = band.price;
            break;
          }
        }
      }

      return result;
    },
    total () {
      return this.studentSubtotal + this.teacherSubtotal + this.volumeSubtotal;
    },
    products () {
      const result = [];

      if ( this.numPlaces && this.defaultPrice ) {
        result.push( {
          productId: this.productId,
          quantity: this.numPlaces,
          pricingScheduleId: this.defaultPrice.pricingScheduleId,
          productFormatId: this.defaultPrice.productFormatId,
          productLicenceTypeId: this.defaultPrice.productLicenceTypeId,
          eventInstanceId: this.instanceId,
        } );
      }

      if ( this.numStudents && this.studentPrice ) {
        result.push( {
          productId: this.productId,
          quantity: this.numStudents,
          pricingScheduleId: this.studentPrice.pricingScheduleId,
          productFormatId: this.studentPrice.productFormatId,
          productLicenceTypeId: this.studentPrice.productLicenceTypeId,
          eventInstanceId: this.instanceId,
        } );
      }

      if ( this.numTeachers && this.teacherPrice ) {
        result.push( {
          productId: this.productId,
          quantity: this.numTeachers,
          pricingScheduleId: this.teacherPrice.pricingScheduleId,
          productFormatId: this.teacherPrice.productFormatId,
          productLicenceTypeId: this.teacherPrice.productLicenceTypeId,
          eventInstanceId: this.instanceId,
        } );
      }

      return result;
    },
    invalidConfig () {
      let result = false;

      if ( ( !this.studentPrice || !this.teacherPrice ) && !this.defaultPrice ) {
        result = true;
      }

      return result;
    },
  },
  watch: {
    selectedBand: {
      immediate: true,
      handler ( val ) {
        const match = this.volumePricingOptions.find( x => x.value === val );
        if ( match ) {
          this.numPlaces = match.quantityLower;
        }
      },
    },
    error ( val ) {
      if ( val ) {
        this.showErrorModal = true;
        window.setTimeout( () => {
          this.error = false;
        }, 6000 );
      }
    },
    submitted ( val ) {
      if ( val ) {
        window.setTimeout( () => {
          window.location = this.basketUrl;
        }, 3000 );
      }
    },
  },
  methods: {
    renderPrice ( input ) {
      return EcommerceModule.renderPrice( input, "gbp", "en-gb" );
    },
    closeErrorModal () {
      this.showErrorModal = false;
    },

    /**
     * Reset the state of the component
     */
    resetState () {
      this.orderLineIds = [];
      this.numStudents = 0;
      this.numTeachers = 0;
      this.agreedToTerms = false;

      this.formData = {
        name: null,
        email: null,
        phone: null,
      };
    },

    /**
     * Refresh any basket components on the page
     */
    refreshBasket () {
      this.$root.$emit( "sw::refreshBasket::request" );
    },

    /**
     * Show the booking modal
     */
    showBookingModal () {
      this.showModal = true;
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/colors.less";

.conference-booker {
  background: @color-accent-2;
  border-radius: 8px;
  color: #fff;
  margin-bottom: 30px;

  @media ( max-width: 991px ) {
    margin-top: 30px;
  }

  &__header {
    padding: 10px 20px;
    border-bottom: 1px solid #fff;
    text-align: center;
  }

  &__heading {
    font-size: 1rem;
    color: #fff;
    margin: 0;

    svg {
      display: inline-block;
      margin-right: 5px;
    }
  }

  &__main {
    padding: 20px
  }

  &__label {
    display: block;
    width: 100%;
  }

  &__price {
    float: right;
  }

  &__prices {
    width: 100%;
  }

  &__quantity {
    padding: 0 15px;
  }

  &__subtotal {
    padding-right: 0;
    min-width: 65px;
    text-align: right;
  }

  &__warning-message {
    padding: 10px 0 10px;
  }

  tfoot td {
    font-weight: 600;
  }

  &__submit {
    background: #fff;
    display: block;
    width: 100%;
    color: @color-accent-2;

    &[disabled]
  {
    background-color: #d3d3d3;
    color: #bfbfbf;
  }

    &:hover {
      background: #eee;

      &[disabled] {
        background-color: lightgray;
        cursor: not-allowed;
      }
    }
  }

  &__contact-us {
    margin-bottom: 0;
    margin-top: 15px;
  }

  /deep/ .custom-control-label {
    width: 100%;
  }

  /deep/ .custom-radio .custom-control-input:checked ~ .custom-control-label::before {
    color: @color-accent-2;
    background-color: #fff;
    border-color: #fff;
  }

  /deep/ .custom-radio .custom-control-input:checked ~ .custom-control-label::after {
    position: absolute;
    background: @color-accent-2;
    border-radius: 100%;
    transform: scale3d(0.5, 0.5, 0.5);
  }

  /deep/ .custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
    color: @color-accent-2;
    background-color: #fff;
    border-color: #fff;
  }

  /deep/ .custom-checkbox .custom-control-input:checked ~ .custom-control-label::after {
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'><path fill='%23ff8470' d='M6.564.75l-3.59 3.612-1.538-1.55L0 4.26l2.974 2.99L8 2.193z'/></svg>");
  }

  /deep/ .custom-modal {
    color: #000;
  }

  .user-type-message {
    display: flex;
    gap: .5rem;
    flex-direction: column;
    margin-bottom: 1rem;
  }
}
</style>
